// SPA-compatible version of Navigation for Vite
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { logout } from '../redux/slices/authSlice';
import { setCurrentTeam } from '../redux/slices/dataSlice';
import type { AppDispatch, RootState } from '../redux/store';
import { useTranslation } from 'react-i18next';
import { SERVER_REGION } from '../../i18n';
import TeamSwitcher from './TeamSwitcher';
import LanguageSwitcher from './LanguageSwitcher';

export default function NavigationSPA() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation('nav');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const [isSubFromStorage, setIsSubFromStorage] = useState(false);
  const [accountTypeFromStorage, setAccountTypeFromStorage] = useState<'regular' | 'master' | 'sub'>('regular');
  const [tokenFromStorage, setTokenFromStorage] = useState<boolean | null>(null);

  useEffect(() => {
    setIsSubFromStorage(localStorage.getItem('isSubAccount') === 'true');
    setAccountTypeFromStorage((localStorage.getItem('accountType') as 'regular' | 'master' | 'sub') || 'regular');
    setTokenFromStorage(!!localStorage.getItem('GrainGuardianToken'));
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMoreOpen(false);
  }, [pathname]);

  // Close "More Features" dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isSubFromRedux = useSelector((state: RootState) => state.auth.isSubAccount);
  const accountTypeFromRedux = useSelector((state: RootState) => state.auth.accountType);
  const tokenFromRedux = useSelector((state: RootState) => state.auth.authenticate);

  const isSubAccount = isSubFromRedux || isSubFromStorage;
  const accountType = accountTypeFromRedux || accountTypeFromStorage;
  const hasToken = tokenFromRedux || tokenFromStorage;

  const handleLogout = async () => {
    await dispatch(logout());
    dispatch(setCurrentTeam(null));
    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 100);
  };

  const isActive = (path: string) => pathname === path;

  const navLinkClass = (path: string, extraCheck?: boolean) =>
    `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer whitespace-nowrap ${
      isActive(path) || extraCheck
        ? 'border-blue-500 text-gray-900'
        : 'border-transparent text-gray-800 hover:border-gray-300 hover:text-gray-900'
    }`;

  const mobileNavLinkClass = (path: string, extraCheck?: boolean) =>
    `block w-full text-left px-4 py-3 text-sm font-medium border-l-4 ${
      isActive(path) || extraCheck
        ? 'border-blue-500 bg-blue-50 text-blue-700'
        : 'border-transparent text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900'
    }`;

  const moreActiveRoutes = ['/session', '/data-download', '/teams', '/account', '/tutorials'];
  const isMoreActive = moreActiveRoutes.some((r) => pathname === r || pathname.startsWith('/teams/'));

  const navItems = hasToken ? (
    <>
      <button onClick={() => navigate('/dashboard')} className={navLinkClass('/dashboard')}>
        {t('dashboard')}
      </button>
      {SERVER_REGION !== 'CN' && (
        <button onClick={() => navigate('/rodent-sensors')} className={navLinkClass('/rodent-sensors')}>
          {t('rodentSensors')}
        </button>
      )}
      {SERVER_REGION !== 'CN' && (
        <button onClick={() => navigate('/map')} className={navLinkClass('/map')}>
          {t('map')}
        </button>
      )}

      {/* More Features dropdown */}
      <div
        ref={moreRef}
        className="relative flex items-center pb-1"
        onMouseEnter={() => setMoreOpen(true)}
        onMouseLeave={() => setMoreOpen(false)}
      >
        <button
          onClick={() => setMoreOpen(true)}
          className={`inline-flex items-center gap-1 px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer whitespace-nowrap ${
            isMoreActive || moreOpen
              ? 'border-blue-500 text-gray-900'
              : 'border-transparent text-gray-800 hover:border-gray-300 hover:text-gray-900'
          }`}
        >
          More Features
          <svg
            className={`h-4 w-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {moreOpen && (
          <div className="absolute top-full left-0 w-52 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1">
            <button
              onClick={() => navigate('/account')}
              className={`block w-full text-left px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                isActive('/account') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {t('account')}
            </button>
            {(accountType === 'master' || accountType === 'regular') && !isSubAccount && (
              <button
                onClick={() => navigate('/teams')}
                className={`block w-full text-left px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                  isActive('/teams') || pathname.startsWith('/teams/') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {t('teams')}
              </button>
            )}
            {!isSubAccount && (
              <button
                onClick={() => navigate('/data-download')}
                className={`block w-full text-left px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                  isActive('/data-download') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {t('dataDownload')}
              </button>
            )}
            <button
              onClick={() => navigate('/session')}
              className={`block w-full text-left px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                isActive('/session') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              {t('session')}
            </button>
            {SERVER_REGION !== 'CN' && (
              <button
                onClick={() => navigate('/tutorials')}
                className={`block w-full text-left px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                  isActive('/tutorials') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {t('tutorialVideos')}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  ) : null;

  const mobileNavItems = hasToken ? (
    <>
      <button onClick={() => navigate('/dashboard')} className={mobileNavLinkClass('/dashboard')}>
        {t('dashboard')}
      </button>
      {SERVER_REGION !== 'CN' && (
        <button onClick={() => navigate('/rodent-sensors')} className={mobileNavLinkClass('/rodent-sensors')}>
          {t('rodentSensors')}
        </button>
      )}
      {SERVER_REGION !== 'CN' && (
        <button onClick={() => navigate('/map')} className={mobileNavLinkClass('/map')}>
          {t('map')}
        </button>
      )}
      {/* More Features section in mobile */}
      <div className="px-4 pt-2 pb-1">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">More Features</p>
      </div>
      <button onClick={() => navigate('/account')} className={mobileNavLinkClass('/account')}>
        {t('account')}
      </button>
      {(accountType === 'master' || accountType === 'regular') && !isSubAccount && (
        <button
          onClick={() => navigate('/teams')}
          className={mobileNavLinkClass('/teams', pathname.startsWith('/teams/'))}
        >
          {t('teams')}
        </button>
      )}
      {!isSubAccount && (
        <button onClick={() => navigate('/data-download')} className={mobileNavLinkClass('/data-download')}>
          {t('dataDownload')}
        </button>
      )}
      <button onClick={() => navigate('/session')} className={mobileNavLinkClass('/session')}>
        {t('session')}
      </button>
      {SERVER_REGION !== 'CN' && (
        <button onClick={() => navigate('/tutorials')} className={mobileNavLinkClass('/tutorials')}>
          {t('tutorialVideos')}
        </button>
      )}
    </>
  ) : null;

  return (
    <nav className="bg-white shadow-lg relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo + desktop nav links */}
          <div className="flex min-w-0">
            <div className="flex-shrink-0 flex items-center">
              <img
                src="/icon.png"
                alt="SmartProbe Logo"
                width={32}
                height={32}
                className="h-8 w-8"
              />
              <span className="ml-2 text-xl font-bold text-gray-900">{t('logo')}</span>
            </div>
            {hasToken && (
              <div className="hidden xl:ml-6 xl:flex xl:items-center xl:space-x-6">
                {navItems}
              </div>
            )}
          </div>

          {/* Right side: team switcher, language, logout (desktop), burger (mobile) */}
          <div className="flex items-center space-x-3 flex-shrink-0 ml-4">
            {hasToken && <TeamSwitcher />}
            {SERVER_REGION === 'CN' && <LanguageSwitcher />}
            {/* Logout visible on desktop where nav links are shown */}
            {hasToken && (
              <button
                onClick={handleLogout}
                className="hidden xl:inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 cursor-pointer"
              >
                {t('logout')}
              </button>
            )}
            {/* Burger — only visible when nav links are collapsed */}
            {hasToken && (
              <button
                onClick={() => setMobileOpen((o) => !o)}
                className="xl:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                aria-expanded={mobileOpen}
                aria-label="Toggle menu"
              >
                {mobileOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Burger dropdown — floats over the page, anchored to the right */}
      {mobileOpen && hasToken && (
        <div className="xl:hidden absolute right-4 top-16 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
          <div className="py-2 border-b border-gray-200">
            {mobileNavItems}
          </div>
          <div className="px-4 py-3">
            <button
              onClick={handleLogout}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none cursor-pointer"
            >
              {t('logout')}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
